import { type FormEvent, useEffect, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Input, Table } from '@/design-system';
import { ProblemAlert } from '@/app/ProblemAlert';
import { formatDate } from '@/lib/format';
import { fieldError, type Problem, toProblem } from '@/lib/problem';
import { credentials, type Issued, type ServiceClient } from './api';
import styles from './CredentialsPage.module.css';

/** READ_CATALOGUE → "Read catalogue". The enum name is the contract; this is only the label. */
function capabilityLabel(capability: string): string {
  const words = capability.toLowerCase().split('_');
  return [words[0]!.charAt(0).toUpperCase() + words[0]!.slice(1), ...words.slice(1)].join(' ');
}

/**
 * Keys for other systems. Administrators only; the backend enforces that on every
 * call. A key is shown once, when issued, and never again: it is stored only as a
 * digest.
 */
export function CredentialsPage() {
  const [clients, setClients] = useState<ServiceClient[] | null>(null);
  const [available, setAvailable] = useState<string[]>([]);
  const [problem, setProblem] = useState<Problem | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [issueProblem, setIssueProblem] = useState<Problem | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [copied, setCopied] = useState(false);

  const [revoking, setRevoking] = useState<ServiceClient | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([credentials.all(), credentials.capabilities()])
      .then(([loaded, capabilities]) => {
        if (cancelled) return;
        setClients(loaded);
        setAvailable(capabilities);
      })
      .catch((error: unknown) => {
        if (!cancelled) setProblem(toProblem(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function reload() {
    setClients(await credentials.all());
  }

  async function issue(event: FormEvent) {
    event.preventDefault();
    setIssueProblem(null);
    setCopied(false);
    try {
      const result = await credentials.issue(name.trim(), description.trim(), chosen);
      setIssued(result);
      setName('');
      setDescription('');
      setChosen([]);
      await reload();
    } catch (error) {
      setIssueProblem(toProblem(error));
    }
  }

  async function copy(key: string) {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the key is on screen to select by hand.
    }
  }

  async function revoke(event: FormEvent) {
    event.preventDefault();
    if (!revoking) return;
    setProblem(null);
    try {
      await credentials.revoke(revoking.clientId, reason.trim());
      setRevoking(null);
      setReason('');
      await reload();
    } catch (error) {
      setProblem(toProblem(error));
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <h1 className={styles.title}>Credentials</h1>
        <p className={styles.muted}>
          Keys for other systems that call this application. A system sends its key as the
          X-Service-Key header and can reach only what its capabilities allow, never the
          pages and endpoints meant for people.
        </p>
      </div>

      {problem && <ProblemAlert problem={problem} title="That was not done" />}

      {issued && (
        <Alert tone="success" title={`Key issued to ${issued.client.name}`}>
          <span className={styles.keyNote}>
            Copy it now. It is shown once and cannot be recovered: only a digest is stored.
          </span>
          <code className={styles.key}>{issued.key}</code>
          <span className={styles.buttons}>
            <Button size="sm" variant="secondary" onClick={() => void copy(issued.key)}>
              {copied ? 'Copied' : 'Copy key'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>
              I have stored it
            </Button>
          </span>
        </Alert>
      )}

      <section aria-labelledby="keys">
        <h2 id="keys" className={styles.sectionTitle}>
          Keys
        </h2>
        {clients === null && !problem ? (
          <p className={styles.muted}>Loading keys.</p>
        ) : (
          <Table
            caption="Every key ever issued, live ones first"
            columns={[
              { key: 'system', header: 'System' },
              { key: 'may', header: 'May' },
              { key: 'key', header: 'Key', nowrap: true },
              // A badge is one phrase; wrapped, it becomes an unreadable blob.
              { key: 'status', header: 'Status', nowrap: true },
              { key: 'action', header: '' },
            ]}
            rows={(clients ?? []).map((client) => ({
              key: client.clientId,
              cells: {
                system: (
                  <span className={styles.stack}>
                    <span className={styles.strong}>{client.name}</span>
                    <span className={styles.muted}>{client.description}</span>
                  </span>
                ),
                may: client.capabilities.map(capabilityLabel).join(', '),
                key: <span className="numeric">…{client.keyHint}</span>,
                status: client.revokedAt ? (
                  <span className={styles.stack}>
                    <Badge tone="danger">Revoked {formatDate(client.revokedAt)}</Badge>
                    <span className={styles.muted}>{client.revocationReason}</span>
                  </span>
                ) : (
                  <Badge tone="success">Live since {formatDate(client.createdAt)}</Badge>
                ),
                action: client.revokedAt ? null : (
                  <Button size="sm" variant="ghost" onClick={() => setRevoking(client)}>
                    Revoke
                  </Button>
                ),
              },
            }))}
            emptyMessage="No keys have been issued."
          />
        )}

        {revoking && (
          <form className={styles.panel} onSubmit={revoke}>
            <h3 className={styles.panelTitle}>Revoke the key for {revoking.name}</h3>
            <p className={styles.muted}>
              It stops working on the system&apos;s next request. Revocation is not undone;
              issue a new key instead.
            </p>
            <Input
              label="Reason"
              helpText="Rotated or compromised? Recorded in the access trail."
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div className={styles.buttons}>
              <Button type="submit" disabled={reason.trim() === ''}>
                Revoke
              </Button>
              <Button type="button" variant="ghost" onClick={() => setRevoking(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </section>

      <section aria-labelledby="issue">
        <h2 id="issue" className={styles.sectionTitle}>
          Issue a key
        </h2>
        {issueProblem && <ProblemAlert problem={issueProblem} title="Not issued" />}
        <form className={styles.form} onSubmit={issue}>
          <Input
            label="System name"
            helpText="What the system is, in your words: nightly-report-export."
            value={name}
            maxLength={64}
            onChange={(event) => setName(event.target.value)}
            {...fieldError(issueProblem, 'name')}
          />
          <Input
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            {...fieldError(issueProblem, 'description')}
          />
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>May</legend>
            {available.map((capability) => (
              <Checkbox
                key={capability}
                label={capabilityLabel(capability)}
                checked={chosen.includes(capability)}
                onChange={(event) =>
                  setChosen(
                    event.target.checked
                      ? [...chosen, capability]
                      : chosen.filter((c) => c !== capability),
                  )
                }
              />
            ))}
          </fieldset>
          <div className={styles.buttons}>
            <Button
              type="submit"
              disabled={name.trim() === '' || description.trim() === '' || chosen.length === 0}
            >
              Issue key
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
